import { useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { ProjectAssetsStore } from "../../../state/ProjectAssetsStore";
import { createMultiSpeakerSequenceAndScriptClip } from "../multiSpeaker/createMultiSpeakerSequenceAndScriptClip";

type SetSearchParamsFn = (next: URLSearchParams, options?: { replace?: boolean }) => void;
type SetStringState = Dispatch<SetStateAction<string | null>>;
type CreateSequenceFn = (args: { projectId: string; title: string }) => Promise<string | null | undefined>;
type GetTrackUrlFn = (args: { trackId: string }) => Promise<string | undefined>;
type CreateSpeakerFn = (args: { projectId: string; name: string }) => Promise<string | null | undefined>;

type AuthContextLike = {
    getUserId?: () => string | null | undefined;
    getCurrentWorkspaceId?: () => string | null | undefined;
    getDefaultWorkspaceId?: () => string | null | undefined;
};

type MultiSpeakerAutocreatePhase = "waiting" | "creating" | "error";

type MultiSpeakerAutocreateSession = {
    sessionKey: string;
    assetIds: string[];
    originCompositionId: string;
    sequenceTitle?: string | null;
    phase: MultiSpeakerAutocreatePhase;
    errorMessage?: string | null;
};

type UseMultiSpeakerAutocreateWorkflowArgs = {
    searchParams: URLSearchParams;
    setSearchParams: SetSearchParamsFn;
    projectId?: string | null;
    selectedCompositionId: string | null;
    setSelectedCompositionId: (compositionId: string | null) => void;
    selectedCompositionTitle: string | null;
    isRestoringComposition: boolean;
    localTrackAvailability: Map<string, boolean>;
    setStudioAutocreatePreferredCompositionId: SetStringState;
    createSequence: CreateSequenceFn;
    getTrackUrl: GetTrackUrlFn;
    createSpeaker?: CreateSpeakerFn;
    authContext: AuthContextLike;
};

const normalizeTrackStatus = (value: unknown): string => String(value ?? "").trim().toLowerCase();
const isTrackReady = (track: any): boolean => normalizeTrackStatus(track?.status) === "ready";

export const useMultiSpeakerAutocreateWorkflow = ({
    searchParams,
    setSearchParams,
    projectId,
    selectedCompositionId,
    setSelectedCompositionId,
    selectedCompositionTitle,
    isRestoringComposition,
    localTrackAvailability,
    setStudioAutocreatePreferredCompositionId,
    createSequence,
    getTrackUrl,
    createSpeaker,
    authContext,
}: UseMultiSpeakerAutocreateWorkflowArgs): { hasPendingMultiSpeakerAutocreate: boolean } => {
    const [session, setSession] = useState<MultiSpeakerAutocreateSession | null>(null);
    const inFlightRef = useRef<Set<string>>(new Set());
    const createSequenceRef = useRef(createSequence);
    const getTrackUrlRef = useRef(getTrackUrl);
    const createSpeakerRef = useRef(createSpeaker);
    const authContextRef = useRef(authContext);

    useEffect(() => {
        createSequenceRef.current = createSequence;
        getTrackUrlRef.current = getTrackUrl;
        createSpeakerRef.current = createSpeaker;
        authContextRef.current = authContext;
    }, [createSequence, getTrackUrl, createSpeaker, authContext]);

    useEffect(() => {
        const shouldAutocreate = searchParams.get("multiSpeakerAutocreate");
        const rawAssetIds = String(searchParams.get("multiSpeakerAssetIds") || "").trim();
        const originCompositionId = String(searchParams.get("multiSpeakerCompositionId") || "").trim();
        const sequenceTitle = String(searchParams.get("multiSpeakerTitle") || "").trim() || null;
        const assetIds = rawAssetIds
            .split(",")
            .map((assetId) => String(assetId || "").trim())
            .filter(Boolean);

        if ((shouldAutocreate !== "1" && shouldAutocreate !== "true") || assetIds.length < 2) return;
        if (!originCompositionId) return;

        const sessionKey = `${originCompositionId}:${assetIds.join(",")}`;
        setSession((prev) => {
            if (prev?.sessionKey === sessionKey) return prev;
            return {
                sessionKey,
                assetIds,
                originCompositionId,
                sequenceTitle,
                phase: "waiting",
                errorMessage: null,
            };
        });
        setStudioAutocreatePreferredCompositionId(originCompositionId);

        const next = new URLSearchParams(searchParams);
        next.delete("multiSpeakerAutocreate");
        next.delete("multiSpeakerAssetIds");
        next.delete("multiSpeakerCompositionId");
        next.delete("multiSpeakerTitle");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, setStudioAutocreatePreferredCompositionId]);

    useEffect(() => {
        setSession(null);
        inFlightRef.current.clear();
    }, [projectId]);

    useEffect(() => {
        if (!session) return;
        if (session.phase !== "waiting") return;
        if (isRestoringComposition) return;
        if (!CompositionService.isInitialized()) return;

        if (selectedCompositionId !== session.originCompositionId) {
            setSelectedCompositionId(session.originCompositionId);
            return;
        }

        const tracks = ProjectAssetsStore.projectTracks || [];
        const orderedTracks: any[] = [];
        for (const assetId of session.assetIds) {
            const matchingTrack = tracks.find((track: any) => String(track?.assetId ?? "").trim() === assetId);
            if (!matchingTrack) return;

            const trackId = String(matchingTrack?.trackId || matchingTrack?.id || "").trim();
            const localUsable = trackId ? localTrackAvailability.get(trackId) === true : false;
            if (!isTrackReady(matchingTrack) && !localUsable) {
                return;
            }

            orderedTracks.push(matchingTrack);
        }

        const sessionKey = session.sessionKey;
        if (inFlightRef.current.has(sessionKey)) return;
        inFlightRef.current.add(sessionKey);
        setSession((prev) => {
            if (!prev || prev.sessionKey !== sessionKey) return prev;
            return { ...prev, phase: "creating", errorMessage: null };
        });

        void (async () => {
            try {
                const userId = String(authContextRef.current.getUserId?.() || "").trim();
                const workspaceId =
                    authContextRef.current.getCurrentWorkspaceId?.() ||
                    authContextRef.current.getDefaultWorkspaceId?.() ||
                    userId ||
                    null;

                await createMultiSpeakerSequenceAndScriptClip({
                    orderedTracks,
                    explicitSequenceTitle: session.sequenceTitle ?? null,
                    projectId: String(projectId || ProjectAssetsStore.projectId || "").trim(),
                    selectedCompositionTitle,
                    userId,
                    workspaceId,
                    createSequence: createSequenceRef.current,
                    getTrackUrl: getTrackUrlRef.current,
                    createSpeaker: createSpeakerRef.current,
                });

                setSession((prev) => (prev?.sessionKey === sessionKey ? null : prev));
                setStudioAutocreatePreferredCompositionId((prev) =>
                    prev === session.originCompositionId ? null : prev
                );
            } catch (error) {
                console.error("[V2Editor] Failed to auto-create sequence/script from multi-speaker upload:", error);
                const message = error instanceof Error && error.message
                    ? error.message
                    : "Failed to create multi-speaker composition.";
                setSession((prev) => {
                    if (!prev || prev.sessionKey !== sessionKey) return prev;
                    return { ...prev, phase: "error", errorMessage: message };
                });
            } finally {
                inFlightRef.current.delete(sessionKey);
            }
        })();
    }, [
        session,
        isRestoringComposition,
        selectedCompositionId,
        setSelectedCompositionId,
        localTrackAvailability,
        setStudioAutocreatePreferredCompositionId,
        projectId,
        selectedCompositionTitle,
    ]);

    return {
        hasPendingMultiSpeakerAutocreate: Boolean(session),
    };
};
